/* eslint-disable @next/next/no-img-element */
'use client'
import { URL_ICON, URL_IMG } from '@/constants/ui'
import { getVND } from '@/utils/helpers'
import { Box, Chip, Grid, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
interface BoxStatusOrder {
  id: string
  data: any
}

type OrderStatus = 'all' | 'pending' | 'processing' | 'complete' | 'canceled' | 'shipping'

export const BoxStatusOrder = ({ id, data }: BoxStatusOrder) => {
  const t = useTranslations('Order')
  return (
    <>
      <Box
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: '#e0e0e0',
          p: 2,
        }}
      >
        <Box
          sx={{
            gap: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Grid container spacing={2} columns={24}>
            <Grid size={{ xs: 10 }}>
              <Box sx={{ fontWeight: 300, fontSize: 14 }}>{t('Mã đơn hàng')}:</Box>
            </Grid>
            <Grid size={{ xs: 14 }}>
              <Box sx={{ fontWeight: 600, fontSize: 14 }}>{data?.number}</Box>
            </Grid>
          </Grid>
          <Grid container spacing={2} columns={24}>
            <Grid size={{ xs: 10 }}>
              <Box sx={{ fontWeight: 300, fontSize: 14 }}>{t('Trạng thái')}:</Box>
            </Grid>
            <Grid size={{ xs: 14 }}>
              <Box sx={{ fontWeight: 600, fontSize: 14 }}>
                {renderOrderStatusChip(data?.status?.toLowerCase(), data?.carrier)}
              </Box>
            </Grid>
          </Grid>
          {/* carrier */}
          <Grid container spacing={2} columns={24}>
            <Grid size={{ xs: 10 }}>
              <Box sx={{ fontWeight: 300, fontSize: 14 }}>{t('Vận chuyển')}:</Box>
            </Grid>
            <Grid size={{ xs: 14 }}>
              <Box sx={{ fontWeight: 600, fontSize: 14 }}>{data?.carrier}</Box>
            </Grid>
          </Grid>
          <Grid container spacing={2} columns={24}>
            <Grid size={{ xs: 10 }}>
              <Box sx={{ fontWeight: 300, fontSize: 14 }}>{t('Ngày đặt hàng')}:</Box>
            </Grid>
            <Grid size={{ xs: 14 }}>
              <Box sx={{ fontWeight: 600, fontSize: 14 }}>
                {dayjs(data?.order_date).add(7, 'hours').format('HH:mm DD/MM/YYYY') || '--'}
              </Box>
            </Grid>
          </Grid>
          {data?.point_info?.order_received_at && (
            <Grid container spacing={2} columns={24}>
              <Grid size={{ xs: 10 }}>
                <Box sx={{ fontWeight: 300, fontSize: 14 }}>{t('Ngày nhận hàng')}:</Box>
              </Grid>
              <Grid size={{ xs: 14 }}>
                <Box sx={{ fontWeight: 600, fontSize: 14 }}>
                  {dayjs(data?.point_info?.order_received_at).add(7, 'hours').format('HH:mm DD/MM/YYYY')}
                </Box>
              </Grid>
            </Grid>
          )}
          <Grid container spacing={2} columns={24}>
            <Grid size={{ xs: 10 }}>
              <Box sx={{ fontWeight: 300, fontSize: 14 }}>{t('Điểm thành viên')}:</Box>
            </Grid>
            <Grid size={{ xs: 14 }}>
              <Box sx={{ fontWeight: 600, fontSize: 14 }}>
                {getVND(data?.point_info?.point_cashback, ' ')}xu
                <Image
                  style={{ display: 'inline-block', margin: '0 4px' }}
                  width={16}
                  height={16}
                  src={URL_IMG + 'favicon-yellow.svg'}
                  alt="Favicon"
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  )
}

/**
 * Hàm hiển thị Chip theo status
 * @param status - 'complete' | 'pending' | 'canceled' | 'processing'
 */
export const renderOrderStatusChip = (status: OrderStatus, carrier?: string) => {
  const defaultChipStyles = {
    padding: '2px 4px',
    borderRadius: '4px',
    fontSize: '0.875rem',
  }

  switch (status) {
    case 'complete':
      return (
        <Chip
          label="Đã giao"
          color="success"
          variant="filled"
          size="small"
          sx={{
            bgcolor: '#E7F8F0',
            color: '#12B76A',
            ...defaultChipStyles,
          }}
          icon={
            <img src={URL_ICON + 'ic-check.svg'} style={{ width: 'auto', height: '1rem' }} alt="Đã giao" />
          }
        />
      )
    case 'processing':
      return (
        <Chip
          label="Chờ xử lý"
          color="warning"
          variant="filled"
          size="small"
          sx={{
            bgcolor: '#FEF0E6',
            color: '#F46A00',
            ...defaultChipStyles,
          }}
          icon={
            <img
              src={URL_ICON + 'ic-pending.svg'}
              style={{ width: 'auto', height: '1rem' }}
              alt="Chờ xử lý"
            />
          }
        />
      )

    case 'pending':
      return (
        <Chip
          label="Chờ xử lý"
          color="warning"
          variant="filled"
          size="small"
          sx={{
            bgcolor: '#FEF0E6',
            color: '#F46A00',
            ...defaultChipStyles,
          }}
          icon={
            <img
              src={URL_ICON + 'ic-pending.svg'}
              style={{ width: 'auto', height: '1rem' }}
              alt="Chờ xử lý"
            />
          }
        />
      )
    case 'canceled':
      return (
        <Chip
          label="Đã hủy"
          color="error"
          variant="filled"
          size="small"
          sx={{
            bgcolor: '#FEECEB',
            color: '#F04438',
            ...defaultChipStyles,
          }}
          icon={
            <img src={URL_ICON + 'ic-close.svg'} style={{ width: 'auto', height: '1rem' }} alt="Đã hủy" />
          }
        />
      )
    case 'shipping':
      return (
        <Chip
          label={
            <Typography
              sx={{
                flexShrink: 0,
                fontSize: '0.875rem',
              }}
            >
              {carrier === 'Lấy tại cửa hàng' ? 'Chờ lấy hàng' : 'Đang vận chuyển'}
            </Typography>
          }
          color="info"
          variant="filled"
          size="small"
          sx={{
            bgcolor: '#D7EEFF',
            color: '#198EE7',
            flexShrink: 0,
            // width: carrier === 'Lấy tại cửa hàng' ? '128px' : '164px',
            width: 'fit-content',
            ...defaultChipStyles,
          }}
          icon={
            <img
              src={URL_ICON + 'ic-transfer.svg'}
              style={{ width: 'auto', height: '1rem' }}
              alt="Đang vận chuyển"
            />
          }
        />
      )
    default:
      return null
  }
}
